import { FC, useMemo } from "react";
import { Flex } from "@radix-ui/themes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/button";
import { api } from "@/modules/api";
import { SmartView, getSmartViewKind } from "./types";
import { SmartViewActionRequestSchema } from "@/domains/openapi/types";

interface SmartViewActionsProps {
  smartView: SmartView;
  onUpdated?: (view: SmartView) => void;
  size?: "1" | "2" | "3";
}

export const SmartViewActions: FC<SmartViewActionsProps> = ({
  smartView,
  onUpdated,
  size = "1",
}) => {
  const smartViewKind = getSmartViewKind(smartView);
  if (smartViewKind === "PULSE") {
    return null;
  }

  const queryClient = useQueryClient();

  const requestBody = useMemo<SmartViewActionRequestSchema>(
    () => ({ associated_space_account_id: smartView.associated_space_account_id }),
    [smartView.associated_space_account_id]
  );

  const handleSuccess = (updated: SmartView) => {
    queryClient.setQueryData(["smart-view", updated.id], updated);
    queryClient.invalidateQueries({ queryKey: ["smart-engine-runs"] });
    onUpdated?.(updated);
  };

  const refreshMutation = useMutation({
    mutationFn: async () => {
      const resp = await api.post("/internal/v1/smart-views/{smart_view_id}/refresh", {
        params: { path: { smart_view_id: smartView.id } },
        body: requestBody,
      });
      return resp.data!;
    },
    onSuccess: handleSuccess,
  });

  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const resp = await api.post("/internal/v1/smart-views/{smart_view_id}/regenerate", {
        params: { path: { smart_view_id: smartView.id } },
        body: requestBody,
      });
      return resp.data!;
    },
    onSuccess: handleSuccess,
  });

  const findMoreMutation = useMutation({
    mutationFn: async () => {
      const resp = await api.post("/internal/v1/smart-views/{smart_view_id}/find-more", {
        params: { path: { smart_view_id: smartView.id } },
        body: requestBody,
      });
      return resp.data!;
    },
    onSuccess: handleSuccess,
  });

  const hasFoundMore = Boolean(smartView.has_found_more);

  return (
    <Flex gap="2">
      <Button
        size={size}
        disabled={refreshMutation.isPending}
        onClick={() => refreshMutation.mutate()}
      >
        {refreshMutation.isPending ? "Refreshing…" : "Refresh"}
      </Button>
      <Button
        size={size}
        disabled={regenerateMutation.isPending}
        onClick={() => regenerateMutation.mutate()}
      >
        {regenerateMutation.isPending ? "Regenerating…" : "Regenerate"}
      </Button>
      <Button
        size={size}
        disabled={findMoreMutation.isPending || hasFoundMore}
        onClick={() => findMoreMutation.mutate()}
      >
        {hasFoundMore ? "Find More (used)" : findMoreMutation.isPending ? "Finding…" : "Find More"}
      </Button>
    </Flex>
  );
};
